import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GlobalService } from '@home/src/app/core/services/global/global.service';
import { ParticipationService } from '@home/src/app/core/services/Participation/participation.service';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { GroupMember } from '@home/src/app/Interface/Dealer';
import {
  ParticipationData,
  ParticipationDetails,
} from '@interface/participation';
import { PopupComponent } from '@home/src/app/shared/components/popup/popup.component';
import { ExcelpdfpopupComponent } from '@home/src/app/shared/components/excelpdfpopup/excelpdfpopup.component';
import { DownloadType } from '@home/src/app/enums/DownloadType';
import { PdfViewerService } from '@home/src/app/core/services/pdf/pdf-viewer.service';
import { PdfButtonClickedEvent } from '@home/src/app/Interface/IPdfGeneration';
import { NadaSpinnerComponent } from '@shared/components/nada-spinner/nada-spinner.component';
import { MONTHS } from '@enums/Date';
import { ErrorPageComponent } from '@home/src/app/shared/components/app-error-page/app-error-page.component';
import { BaseErrorComponent } from '../baseerror/baseerror.component';
import { CommentsService } from '../../core/services/comments/comments.service';
import { CustomDateSelectComponent } from '../../shared/components/custom-date-select/custom-date-select.component';

@Component({
  selector: 'app-participation',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    PopupComponent,
    ExcelpdfpopupComponent,
    NadaSpinnerComponent,
    ErrorPageComponent,
    CustomDateSelectComponent,
  ],
  templateUrl: './participation.component.html',
  styleUrl: './participation.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class ParticipationComponent
  extends BaseErrorComponent
  implements OnInit, OnDestroy
{
  @ViewChild('pdfExcelPopup') pdfExcelPopup!: ExcelpdfpopupComponent;
  protected readonly unsubscribe$ = new Subject<void>();
  selectedDealer: GroupMember | null = null;
  groupId: number | null = null;
  selectedDropDownValue: any;
  period: boolean = true;
  noDataFound: boolean = false;
  successMessage: string = '';
  errorMessage: string = '';
  public currentDate: string = `${MONTHS[new Date().getMonth()]} ${new Date().getDate()}, ${new Date().getFullYear()}`;
  public processingMonth: string = '';
  public groupName: string | null = null;
  statementParticipation: string = '0%';
  participationDetails: ParticipationDetails | null = null;
  participationGrid: ParticipationData[] = [];
  isSpinner: boolean = false;
  mode: 'excel' | 'pdf' = 'excel';
  showExcelPdfPopUp = false;
  popupTitle: string = 'Participation';
  reportTitle: string = 'Participation';
  isPageLayoutEnabled: boolean = false; // Track page layout toggle
  // Define options for Excel and PDF
  pdfOptions = [
    { label: 'N20 Header', checked: true },
    { label: 'Participation Report', checked: true },
  ];
  selectedOptions: string[] = [];

  showPdfExportMessage: boolean = false;
  pdfExportMessage: string = '';

  minDate = new Date(new Date().getFullYear() - 1, new Date().getMonth() - 1);
  maxDate = new Date(new Date().getFullYear(), new Date().getMonth() - 1);
  selectedDate: Date = this.maxDate;

  get processingMonthYear(): string {
    if (!this.selectedDate) return '';
    const month = MONTHS[this.selectedDate.getMonth()];
    return `${month} ${this.selectedDate.getFullYear()}`;
  }

  constructor(
    private globalService: GlobalService,
    private readonly participationService: ParticipationService,
    private cdr: ChangeDetectorRef,
    private readonly pdfViewerService: PdfViewerService,
    private commentService: CommentsService,
  ) {
    super();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async ngOnInit() {
    this.globalService.selectedDealer$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(async (data) => {
        this.selectedDealer = data;
        this.onDateSelected(this.selectedDate);
      });

    this.participationService.gridParticipation$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        if (state != null) {
          this.participationDetails = state.participationData;
          this.participationGrid = this.participationDetails?.participationList;
          this.statementParticipation =
            this.participationDetails?.statementParticipation;
          this.noDataFound = this.participationGrid.length === 0;
        } else {
          this.participationDetails = null;
          this.participationGrid = [];
          this.noDataFound = true;
        }
      });
  }

  async onDateSelected(date: Date) {
    this.selectedDate = date;
    await this.getParticipationData();
    this.cdr.detectChanges();
  }

  async getParticipationData() {
    this.resetErrorPage();
    if (!this.selectedDate || !this.selectedDealer) return;
    this.isSpinner = true;
    const groupCode = this.selectedDealer?.groupMemberCode.slice(0, -2);
    const response = await this.participationService.fetchParticipationGridData(
      {
        Stmt_Month: this.selectedDate.getMonth() + 1,
        Group_Code: groupCode,
        Stmt_Year: this.selectedDate.getFullYear(),
      },
    );
    this.isSpinner = false;
    if (response.statusCode !== 200) {
      this.setErrorPage(response.statusCode);
    } else if (!response.data) {
      this.setErrorPage(204);
    }
  }

  openPopup(mode: 'excel' | 'pdf') {
    this.mode = mode;
    this.showExcelPdfPopUp = true;
  }

  closePopup() {
    this.showExcelPdfPopUp = false;
  }

  async handlePrimaryButtonClick(event: PdfButtonClickedEvent) {
    if (!event.selectedOptions.length) {
      this.showExcelPdfPopUp = false;
      this.showErrorModal = true;
      this.errorMessage = 'At least one checkbox option must be selected';
      return;
    }
    if (event.mode === DownloadType.Pdf) {
      if (this.cdr) this.cdr.detectChanges();
      const mainDiv = await this.preparePdfContent(event);
      if (mainDiv) {
        const host = document.getElementById('pdf-render-host');
        host?.appendChild(mainDiv);
        const pdfBlob = await this.pdfViewerService.generatePdfFromTable(
          mainDiv,
          event.orientation?.toLowerCase() === 'landscape'
            ? 'landscape'
            : 'portrait',
          event.pageSize || 'A4',
          event.reportTitle,
          this.selectedDealer?.orgNickName ?? '',
          0.95,
        );

        if (pdfBlob) {
          this.globalService.showToast(
            'PDF report generated successfully.',
            'success',
          );
          this.pdfExcelPopup.isLoading = false;
          this.showExcelPdfPopUp = false;
          this.cdr.detectChanges();
          this.commentService.addLocalNotification({
            title: 'Report Generated',
            description: `Successfully generated PDF report for ${this.selectedDealer?.orgNickName}. Click on View PDF to view the report.`,
            type: 'Participation',
            notificationId: new Date().toLocaleString(),
            createdDate: new Date().toLocaleString(),
            isRead: false,
            isServer: false,
            pdfUrl: URL.createObjectURL(pdfBlob),
          });
          this.pdfExportMessage =
            'PDF report generated successfully. You can download it from the notifications panel.';
        } else {
          this.globalService.showToast(
            'Failed to generate PDF report. Please try again.',
            'error',
          );
          this.pdfExcelPopup.isLoading = false;
          this.showExcelPdfPopUp = false;
          this.pdfExportMessage =
            'Failed to generate PDF report. Please try again.';
          this.cdr.detectChanges();
        }
      } else {
        this.pdfExportMessage =
          'Failed to generate PDF report. Please try again.';
        this.globalService.showToast(
          'Failed to generate PDF report. Please try again.',
          'error',
        );
      }
      this.closePopup();
      if (event.mode === DownloadType.Pdf) {
        this.showPdfExportMessage = true;
        this.cdr.detectChanges();
      }
    }
  }

  private prepareOffscreenNode(node: HTMLElement): HTMLElement {
    node.style.position = 'absolute';
    node.style.left = '-10000px';
    node.style.top = '0';
    node.style.visibility = 'visible';
    node.style.pointerEvents = 'none';
    return node;
  }

  private preparePdfContent(event: any): HTMLElement | null {
    const mainDiv = document.createElement('div');
    mainDiv.id = 'main-div';
    this.prepareOffscreenNode(mainDiv);
    const bannerDiv = this.pdfViewerService.createBannerContent(
      event.reportTitle,
    );
    if (!bannerDiv) return null;

    if (event.selectedOptions.includes('N20 Header')) {
      mainDiv.appendChild(
        this.pdfViewerService.createHeader(
          this.selectedDealer,
          bannerDiv,
          event.reportTitle,
        ),
      );
    }
    if (event.selectedOptions.includes('Participation Report')) {
      const filterDiv = this.pdfViewerService.FilterContent();
      filterDiv.innerHTML = `
      <div style="text-align: left; padding: 2px"><b>Participation Status:</b> ${this.statementParticipation}</div>
      <div style="text-align: left; padding: 2px"><b>Group Code:</b> ${this.selectedDealer?.groupMemberCode?.slice(0, -2)}</div>
      <div style="text-align: left; padding: 2px"><b>Today's Date:</b> ${this.currentDate}</div>
      <div style="text-align: left; padding: 2px"><b>Selected Date:</b> ${this.processingMonthYear}</div>`;
      filterDiv.id = 'filtersDiv';
      mainDiv.appendChild(filterDiv);
      const tableContent = document
        .getElementById('pdf-table-content')
        ?.cloneNode(true) as HTMLElement;
      if (!tableContent) return null;
      // const clonedTableContent = tableContent.cloneNode(true) as HTMLElement;
      tableContent.id = 'contentDiv';

      mainDiv.appendChild(tableContent);
    }
    return mainDiv;
  }

  onShowPdf() {
    this.showPdfExportMessage = false;
    this.pdfExportMessage = '';
    this.commentService.setNotificationOpen(true);
  }
}
